import { Level, LevelObject } from "./types";

interface LevelBuilder {
  objects: LevelObject[];
  currentX: number;
  width: number;
}

class LevelBuilderImpl implements LevelBuilder {
  objects: LevelObject[] = [];
  currentX = 0;
  width = 0;
  private bookmarks = new Map<string, number>();

  constructor(startX = 0) {
    this.currentX = startX;
  }

  // Move to next position with offset
  move(offset: number): this {
    this.currentX += offset;
    return this;
  }

  // Add a platform block
  block(width = 1, height = 1, y = 0): this {
    this.objects.push({ type: "platform", x: this.currentX, y, width, height });
    this.currentX += width;
    this.width = Math.max(this.width, this.currentX);
    return this;
  }

  // Add a spike
  spike(width = 1, y = 1): this {
    this.objects.push({ type: "spike", x: this.currentX, y, width });
    this.currentX += width;
    this.width = Math.max(this.width, this.currentX);
    return this;
  }

  // Add multiple spikes in a row
  spikes(countOrBookmark: number | string, width = 1, y = 1): this {
    if (typeof countOrBookmark === "string") {
      // Fill with spikes until bookmark
      const targetPosition = this.bookmarks.get(countOrBookmark);
      if (targetPosition !== undefined) {
        while (this.currentX < targetPosition) {
          this.objects.push({ type: "spike", x: this.currentX, y, width: 1 });
          this.currentX += 1;
          this.width = Math.max(this.width, this.currentX);
        }
      }
    } else {
      // Fill with exact count
      for (let i = 0; i < countOrBookmark; i++) {
        this.spike(width, y);
      }
    }
    return this;
  }

  // Add a platform at a specific height
  platform(width: number, height = 1, y = 0): this {
    return this.block(width, height, y);
  }

  // Add a stair step (platform that goes up by 1)
  step(width = 2, height = 1): this {
    const currentY = this.getLastPlatformY() + 1;
    return this.block(width, height, currentY);
  }

  // Add a stair step down (platform that goes down by 1)
  stepDown(width = 2, height = 1): this {
    const currentY = Math.max(0, this.getLastPlatformY() - 1);
    return this.block(width, height, currentY);
  }

  // Jump pad section - gap followed by platform
  jump(gapWidth: number, platformWidth = 2, platformY = 0): this {
    return this.move(gapWidth).block(platformWidth, 1, platformY);
  }

  // High jump section - gap followed by higher platform
  highJump(gapWidth: number, platformWidth = 2, platformY = 2): this {
    return this.move(gapWidth).block(platformWidth, 1, platformY);
  }

  // Repeat a pattern
  repeat(count: number, pattern: (builder: this) => this): this {
    for (let i = 0; i < count; i++) {
      pattern(this);
    }
    return this;
  }

  // Set position manually
  setX(x: number): this {
    this.currentX = x;
    return this;
  }

  // Set level width manually
  setWidth(width: number): this {
    this.width = width;
    return this;
  }

  // Bookmark system
  bookmark(name: string): this {
    this.bookmarks.set(name, this.currentX);
    return this;
  }

  // Go back to bookmark
  gotoBookmark(name: string): this {
    const position = this.bookmarks.get(name);
    if (position !== undefined) {
      this.currentX = position;
    }
    return this;
  }

  // Get the Y position of the last platform (for relative positioning)
  private getLastPlatformY(): number {
    const platforms = this.objects.filter((obj) => obj.type === "platform");
    if (platforms.length === 0) {
      return 0;
    }
    // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
    return platforms[platforms.length - 1].y;
  }

  // Build the final level
  build(id: string, name: string, bpm = 120, beatOffset = 0): Level {
    return {
      id,
      name,
      bpm,
      beatOffset,
      grid: {
        width: this.width, // No padding at the end
      },
      objects: [...this.objects],
    };
  }
}

/*
 * Using this level builder is harder than writing assembly
 * and is maybe harder than programming with a raw turing machine.
 *
 * A better v2 might instead have a .setReferencePoint() method instead of automatically adjusting the x position.
 */
export function createLevelBuilder(startX = 0): LevelBuilderImpl {
  return new LevelBuilderImpl(startX);
}

// Helper function for common patterns
export const patterns = {
  // Stair climbing pattern
  stairs: (builder: LevelBuilderImpl, steps: number, stepWidth = 2) => {
    for (let i = 0; i < steps; i++) {
      builder.step(stepWidth);
    }
    return builder;
  },

  // Spike field pattern
  spikeField: (builder: LevelBuilderImpl, count: number, spacing = 0) => {
    for (let i = 0; i < count; i++) {
      builder.spike().move(spacing);
    }
    return builder;
  },

  // Platform jumping pattern
  platformJumps: (
    builder: LevelBuilderImpl,
    count: number,
    gapSize = 2,
    platformSize = 2,
  ) => {
    for (let i = 0; i < count; i++) {
      builder.jump(gapSize, platformSize);
    }
    return builder;
  },
};
